import dayjs from 'dayjs';

/**
 * The E-Invoice page's rules and API shapes (plan §7.2,
 * `documentation/implementation_plan_e_invoice_module.md`).
 *
 * The backend decides; this mirrors its rules only to offer the actions it would accept, so a
 * button the backend would refuse is not shown. Every user-visible text is a translation key.
 */

export const E_INVOICE_STATUSES = [
    'PENDING',
    'SUBMITTING',
    'SUBMITTED',
    'FAILED_RETRYABLE',
    'FAILED',
    'BLOCKED',
    'SKIPPED',
    'IMPORTED',
] as const;
export type EInvoiceStatus = (typeof E_INVOICE_STATUSES)[number];

export type EInvoiceOperation = 'ISSUE' | 'REVERSE' | 'CANCEL';

export interface EInvoiceInvoiceInfo {
    date: string | null;
    type: string | null;
    clientName: string | null;
    amount: string | null;
    contractNumber: string | null;
}

export interface EInvoiceSubmission {
    id: number;
    invoiceId: number;
    invoiceNumber: string;
    operation: EInvoiceOperation;
    status: EInvoiceStatus;
    provider: string;
    providerSeries: string | null;
    providerNumber: string | null;
    errorCode: string | null;
    errorMessage: string | null;
    resolutionNote: string | null;
    attempts: number;
    nextAttemptAt: string | null;
    lastAttemptAt: string | null;
    submittedAt: string | null;
    centralCopiedAt: string | null;
    requestedBy: number | null;
    createdAt: string;
    updatedAt: string;
    invoice: EInvoiceInvoiceInfo | null;
    /** Administrators only, on the detail request. */
    requestPayload?: string | null;
    responseRaw?: string | null;
    payloadsHidden?: boolean;
}

export interface SubmissionListResponse {
    success: boolean;
    data: EInvoiceSubmission[];
    total: number;
    page: number;
    pageSize: number;
}

export type DriftState =
    | { drifted: false; submissionId?: number | null }
    | { drifted: true; invoiceNumber: string; providerNumber: string; submissionId: number | null };

/** Whether the installation sends for real or answers through the Delphi-based simulator. */
export interface SendingMode {
    live: boolean;
    simulationScenario: string | null;
}

export interface EInvoiceSummary {
    counts: Partial<Record<EInvoiceStatus, number>>;
    lastSubmittedAt: string | null;
    drift: DriftState;
    autoSubmit: boolean;
    autoSubmitBlockers: string[];
    centralCopyEnabled: boolean;
    sending: SendingMode;
}

export interface EInvoiceSettings {
    provider: string;
    smartBillUsername: string | null;
    seriesName: string | null;
    receiptMode: boolean;
    receiptSeries: string | null;
    submitAfterDays: number;
    cancelWindowDays: number;
    sendsPerSecond: number;
    startDate: string | null;
    autoSubmit: boolean;
    hasToken: boolean;
    legacyImportedAt: string | null;
    updatedAt: string | null;
    autoSubmitBlockers: string[];
    centralCopyEnabled: boolean;
    sending: SendingMode;
}

/** One series a send would use, as found (or not) in the real SmartBill account. */
export interface SeriesCheck {
    role: 'invoice' | 'receipt';
    name: string;
    found: boolean;
    nextNumber: string | null;
}

/** An invoice series of the SmartBill account, with the number its next invoice would get. */
export interface AccountSeries {
    name: string;
    nextNumber: string | null;
}

/** The connection test: always the real SmartBill account, read-only (`GET /series`). */
export interface ConnectionTestResult {
    /** Credentials accepted and every configured series found. */
    ok: boolean;
    /** Credentials and tax code accepted. */
    connected: boolean;
    httpStatus: number | null;
    errorCode: string | null;
    errorMessage: string | null;
    series: SeriesCheck[];
    /** Every invoice series in the account; empty when SmartBill refused. */
    accountInvoiceSeries: AccountSeries[];
}

/** A translation key with its interpolation values. */
export interface TranslatedText {
    key: string;
    params?: Record<string, string>;
}

/**
 * The headline of a connection test: worked, no invoice series set, a series is missing, or
 * SmartBill refused.
 */
export function connectionTestMessage(result: ConnectionTestResult): TranslatedText {
    if (result.ok) {
        return { key: 'eInvoice.settings.testOk' };
    }

    if (result.connected) {
        return result.errorCode === 'SERIES_NOT_SET'
            ? { key: 'eInvoice.settings.testSeriesNotSet' }
            : { key: 'eInvoice.settings.testSeriesMissing' };
    }

    return {
        key: 'eInvoice.settings.testRefused',
        params: { reason: result.errorMessage ?? result.errorCode ?? '' },
    };
}

/** One line per checked series: found with SmartBill's next number, or missing. */
export function seriesCheckText(check: SeriesCheck): TranslatedText {
    const outcome = check.found ? 'found' : 'missing';

    return {
        key: `eInvoice.settings.testSeries.${check.role}.${outcome}`,
        params: { name: check.name, nextNumber: check.nextNumber ?? '-' },
    };
}

/** One line per invoice series of the account: the series and number of its next invoice. */
export function accountSeriesText(series: AccountSeries): TranslatedText {
    return {
        key: 'eInvoice.settings.testAccountSeriesItem',
        params: { name: series.name, nextNumber: series.nextNumber ?? '-' },
    };
}

export interface LegacyRowReference {
    invoiceNumber: string;
}

export interface LegacyImportResult {
    dryRun: boolean;
    legacyTableFound: boolean;
    legacyRows: number;
    imported: { issue: number; reverse: number; cancel: number; rejected: number };
    alreadyPresent: number;
    supersededRows: number;
    unmatched: LegacyRowReference[];
    ambiguous: LegacyRowReference[];
    rejected: Array<{ invoiceNumber: string; errorText: string }>;
    cancelsLeftForWorker: string[];
    proposedStartDate: string | null;
    startDate: string | null;
    startDateSource: 'ADMIN' | 'KEPT' | 'IMPORT' | 'NONE';
    setup: { found: boolean; legacyService: string | null; copiedFields: string[]; token: string };
}

export interface InvoiceLookupRow extends EInvoiceInvoiceInfo {
    id: number;
    invoiceNumber: string;
}

export interface PreviewResult {
    invoiceId: number;
    invoiceNumber: string;
    operation: EInvoiceOperation;
    waiting: boolean;
    error: { code: string; messageKey: string } | null;
    request: { method: string; path: string; body: string | null } | null;
}

/** `GET /invoices/:id/preview?format=xml`: the CIUS-RO document, or why there is none. */
export interface XmlPreviewResult {
    invoiceId: number;
    invoiceNumber: string;
    operation: EInvoiceOperation;
    waiting: boolean;
    errors: { code: string; messageKey: string }[];
    xml: string | null;
}

export interface LiveTestOutcome {
    attempted: boolean;
    simulated: boolean;
    status: EInvoiceStatus | null;
    providerSeries: string | null;
    providerNumber: string | null;
    /** A module key, or the provider's own text. */
    message: string | null;
}

export interface TestResult {
    preview: PreviewResult;
    file: { name: string; directory: string } | null;
    live: LiveTestOutcome | null;
}

export const E_INVOICE_PAGE_SIZE = 50;

/**
 * Window event the E-Invoice page raises after a change (an action, saved settings), so the
 * header badge reloads at once instead of at its next poll.
 */
export const E_INVOICE_CHANGED_EVENT = 'e-invoice:changed';

/** Sends every invoice not sent yet, now (the handover question and the queue's button). */
export const E_INVOICE_RUN_PATH = '/e-invoice/run';

/** The backend accepts these as `role` for its admin-only actions (decision D8). */
export function isAdminRole(role: string | undefined): boolean {
    return role?.toUpperCase() === 'ADMIN';
}

const STATUS_COLORS: Record<EInvoiceStatus, string> = {
    PENDING: 'blue',
    SUBMITTING: 'geekblue',
    SUBMITTED: 'green',
    FAILED_RETRYABLE: 'orange',
    FAILED: 'red',
    BLOCKED: 'volcano',
    SKIPPED: 'default',
    IMPORTED: 'cyan',
};

export function statusColor(status: EInvoiceStatus): string {
    return STATUS_COLORS[status];
}

export function statusLabelKey(status: EInvoiceStatus): string {
    return `eInvoice.status.${status}`;
}

export function operationLabelKey(operation: EInvoiceOperation): string {
    return `eInvoice.operation.${operation}`;
}

/** Rows a person may send again (backend `canRetry`). */
const RETRYABLE: readonly EInvoiceStatus[] = ['FAILED', 'FAILED_RETRYABLE', 'BLOCKED'];
/** Rows a person may resolve by hand (backend `canMarkHandled`). */
const HANDLEABLE: readonly EInvoiceStatus[] = [
    'PENDING',
    'FAILED',
    'FAILED_RETRYABLE',
    'BLOCKED',
    'SKIPPED',
];
const HANDLED_MANUALLY_CODE = 'HANDLED_MANUALLY';

export interface SubmissionActions {
    retry: boolean;
    sendNow: boolean;
    markHandled: boolean;
}

/**
 * The manual actions offered on a row. All are admin-only. *Send now* is for a row still waiting
 * (a failed one is *retried*); a cancel is never sent by hand, the worker sends it in its window.
 */
export function submissionActions(
    submission: Pick<EInvoiceSubmission, 'status' | 'operation' | 'errorCode'>,
    isAdmin: boolean,
): SubmissionActions {
    if (!isAdmin) {
        return { retry: false, sendNow: false, markHandled: false };
    }

    return {
        retry: RETRYABLE.includes(submission.status),
        sendNow: submission.status === 'PENDING' && submission.operation !== 'CANCEL',
        markHandled:
            HANDLEABLE.includes(submission.status) &&
            submission.errorCode !== HANDLED_MANUALLY_CODE,
    };
}

export interface SubmissionFilter {
    status: EInvoiceStatus | null;
    from: string | null;
    to: string | null;
    search: string;
    page: number;
}

export const EMPTY_SUBMISSION_FILTER: SubmissionFilter = {
    status: null,
    from: null,
    to: null,
    search: '',
    page: 1,
};

/** `GET /e-invoice/submissions` query parameters; empty filters are left out. */
export function buildSubmissionQuery(filter: SubmissionFilter): Record<string, string | number> {
    const search = filter.search.trim();

    return {
        ...(filter.status === null ? {} : { status: filter.status }),
        ...(filter.from === null ? {} : { from: filter.from }),
        ...(filter.to === null ? {} : { to: filter.to }),
        ...(search === '' ? {} : { search }),
        page: filter.page,
        pageSize: E_INVOICE_PAGE_SIZE,
    };
}

/** One grid row: the submission with its invoice's fields brought up a level. */
export interface QueueRow {
    id: number;
    invoiceId: number;
    invoiceNumber: string;
    invoiceDate: string | null;
    invoiceType: string | null;
    clientName: string | null;
    amount: number | null;
    operation: EInvoiceOperation;
    status: EInvoiceStatus;
    providerReference: string | null;
    errorCode: string | null;
    errorMessage: string | null;
    attempts: number;
    lastAttemptAt: string | null;
    submission: EInvoiceSubmission;
}

function toAmount(value: string | null | undefined): number | null {
    if (value === null || value === undefined || value.trim() === '') {
        return null;
    }

    const amount = Number(value);

    return Number.isFinite(amount) ? amount : null;
}

/** `"<series> <number>"` as SmartBill numbered it; null while it has no number. */
export function providerReference(
    submission: Pick<EInvoiceSubmission, 'providerSeries' | 'providerNumber'>,
): string | null {
    if (submission.providerNumber === null) {
        return null;
    }

    return [submission.providerSeries, submission.providerNumber]
        .filter(part => part !== null)
        .join(' ');
}

export function toQueueRow(submission: EInvoiceSubmission): QueueRow {
    return {
        id: submission.id,
        invoiceId: submission.invoiceId,
        invoiceNumber: submission.invoiceNumber,
        invoiceDate: submission.invoice?.date ?? null,
        invoiceType: submission.invoice?.type ?? null,
        clientName: submission.invoice?.clientName ?? null,
        amount: toAmount(submission.invoice?.amount),
        operation: submission.operation,
        status: submission.status,
        providerReference: providerReference(submission),
        errorCode: submission.errorCode,
        errorMessage: submission.errorMessage,
        attempts: submission.attempts,
        lastAttemptAt: submission.lastAttemptAt,
        submission,
    };
}

/** Invoice amounts are shown to the cent: the e-invoice carries them exactly. */
export function formatInvoiceAmount(amount: number | null): string {
    return amount === null ? '-' : amount.toFixed(2);
}

const MESSAGE_KEY_PATTERN = /^(eInvoice\.errors|errors)\.[A-Za-z]+$/;

export function isMessageKey(text: string): boolean {
    return MESSAGE_KEY_PATTERN.test(text);
}

/** A row's error: a key the module wrote, or the provider's own text (SmartBill's `errorText`). */
export type ErrorDescription = { kind: 'key'; key: string } | { kind: 'text'; text: string };

export function describeSubmissionError(
    submission: Pick<EInvoiceSubmission, 'errorCode' | 'errorMessage'>,
): ErrorDescription | null {
    const text = submission.errorMessage ?? submission.errorCode;

    if (text === null || text.trim() === '') {
        return null;
    }

    return isMessageKey(text) ? { kind: 'key', key: text } : { kind: 'text', text };
}

/** The message of a live test: a key the module wrote, or the provider's own text. */
export function describeLiveMessage(message: string | null): ErrorDescription | null {
    if (message === null || message.trim() === '') {
        return null;
    }

    return isMessageKey(message) ? { kind: 'key', key: message } : { kind: 'text', text: message };
}

/** The key carried by a failed request (see `useApiRequest`), or `fallback`. */
export function apiErrorKey(error: unknown, fallback: string): string {
    return error instanceof Error && isMessageKey(error.message) ? error.message : fallback;
}

/** A stored request or reply, indented when it is JSON. */
export function formatPayload(payload: string | null | undefined): string {
    if (payload === null || payload === undefined || payload === '') {
        return '';
    }

    try {
        return JSON.stringify(JSON.parse(payload), null, 2);
    } catch {
        return payload;
    }
}

const XML_OPENING_TAG = /^<[^/?!][^>]*(?<!\/)>$/;

/** One XML element per line, indented by depth; an element holding only text stays on one line. */
export function formatXml(xml: string): string {
    let depth = 0;

    return xml
        .replace(/>\s+</g, '><')
        .split(/(?<=>)(?=<)/)
        .map(line => {
            if (line.startsWith('</')) {
                depth = Math.max(0, depth - 1);
            }

            const indented = `${'  '.repeat(depth)}${line}`;

            if (XML_OPENING_TAG.test(line)) {
                depth += 1;
            }

            return indented;
        })
        .join('\n');
}

export interface StatusCount {
    status: EInvoiceStatus;
    count: number;
}

/**
 * Why this user cannot send the invoices not sent yet, now (`POST /e-invoice/run`), as translation
 * keys; empty when the button may be pressed. Same rule as the backend's `runNowRefusal` - the
 * settings are ready, and sending is live or the user is an administrator - plus a run already
 * sending. The button is always shown, disabled with these reasons beside it.
 */
export function runNowBlockers(
    summary: EInvoiceSummary | undefined,
    isAdmin: boolean,
    runActive: boolean,
): string[] {
    const blockers = runActive ? ['eInvoice.run.blocked.running'] : [];

    if (summary === undefined) {
        return [...blockers, 'eInvoice.run.blocked.loading'];
    }

    blockers.push(...summary.autoSubmitBlockers);

    if (!summary.sending.live && !isAdmin) {
        blockers.push('eInvoice.run.blocked.simulated');
    }

    return blockers;
}

/** Whether this user may send the invoices not sent yet, now, when no run is sending. */
export function canRunNow(summary: EInvoiceSummary | undefined, isAdmin: boolean): boolean {
    return runNowBlockers(summary, isAdmin, false).length === 0;
}

/**
 * Whether the cash drawer handover asks to send the e-invoices, as Delphi asked "Doriti exportarea
 * eFacturilor?" when the day was closed: only where the module is available, its settings are ready
 * and sending is live. A simulated installation never asks the cashier.
 */
export function asksAtHandover(moduleAvailable: boolean, summary: EInvoiceSummary | undefined) {
    return moduleAvailable && summary?.sending.live === true && canRunNow(summary, false);
}

/** The latest run and how far it has got, for every user (`GET /e-invoice/run/status`). */
export const E_INVOICE_RUN_STATUS_PATH = '/e-invoice/run/status';

/** How often the page asks for the run's progress: often while it goes, rarely otherwise. */
export const RUN_POLL_ACTIVE_MS = 2_000;
export const RUN_POLL_IDLE_MS = 15_000;

export type EInvoiceRunSource = 'HANDOVER' | 'PAGE' | 'SCHEDULE';
export type EInvoiceRunStatus = 'RUNNING' | 'FINISHED' | 'FAILED' | 'INTERRUPTED';

/** How the rows a run looked at ended, in the order the page lists them. */
export const RUN_COUNT_KEYS = [
    'submitted',
    'failed',
    'retrying',
    'skipped',
    'blocked',
    'waiting',
] as const;
export type RunCountKey = (typeof RUN_COUNT_KEYS)[number];

export interface EInvoiceRun {
    id: number;
    source: EInvoiceRunSource;
    status: EInvoiceRunStatus;
    requestedByName: string | null;
    startedAt: string;
    updatedAt: string;
    finishedAt: string | null;
    /** Queued rows the run found; `processed` of them are dealt with. */
    total: number;
    processed: number;
    counts: Record<RunCountKey, number>;
    currentInvoiceNumber: string | null;
    errorCode: string | null;
    /** Translation key of why the run failed. */
    errorMessage: string | null;
}

export type RunAlertType = 'info' | 'success' | 'warning' | 'error';

const RUN_ALERT_TYPES: Record<EInvoiceRunStatus, RunAlertType> = {
    RUNNING: 'info',
    FINISHED: 'success',
    FAILED: 'error',
    INTERRUPTED: 'warning',
};

function formatRunTime(value: string): string {
    return dayjs(value).format('YYYY-MM-DD HH:mm');
}

export function isRunActive(run: EInvoiceRun | null | undefined): boolean {
    return run?.status === 'RUNNING';
}

export function runPollInterval(run: EInvoiceRun | null | undefined): number {
    return isRunActive(run) ? RUN_POLL_ACTIVE_MS : RUN_POLL_IDLE_MS;
}

/** How far the run has got, 0-100; an ended run with nothing to do is complete. */
export function runPercent(run: EInvoiceRun): number {
    if (run.total === 0) {
        return isRunActive(run) ? 0 : 100;
    }

    return Math.min(100, Math.floor((run.processed * 100) / run.total));
}

/**
 * Whether the run the page was following has ended since the last reading, so the queue and the
 * counts are read again: it is no longer running, or another run has taken its place.
 */
export function runEnded(previous: EInvoiceRun | null, next: EInvoiceRun | null): boolean {
    if (previous === null || !isRunActive(previous)) {
        return false;
    }

    return next === null || next.id !== previous.id || !isRunActive(next);
}

/** Green when everything went, amber when some invoices were refused or blocked. */
export function runAlertType(run: EInvoiceRun): RunAlertType {
    const needsAttention = run.counts.failed + run.counts.blocked > 0;

    return run.status === 'FINISHED' && needsAttention ? 'warning' : RUN_ALERT_TYPES[run.status];
}

/** The run's headline: how far it has got, or how it ended. */
export function runHeadline(run: EInvoiceRun): TranslatedText {
    const counted = { processed: String(run.processed), total: String(run.total) };
    const endedAt = formatRunTime(run.finishedAt ?? run.updatedAt);

    switch (run.status) {
        case 'RUNNING':
            return run.total === 0
                ? { key: 'eInvoice.runStatus.preparing' }
                : { key: 'eInvoice.runStatus.running', params: counted };
        case 'FINISHED':
            return run.total === 0
                ? { key: 'eInvoice.runStatus.nothingToSend', params: { time: endedAt } }
                : { key: 'eInvoice.runStatus.finished', params: { ...counted, time: endedAt } };
        case 'FAILED':
            return { key: 'eInvoice.runStatus.failed', params: { time: endedAt } };
        case 'INTERRUPTED':
            return {
                key: 'eInvoice.runStatus.interrupted',
                params: { time: formatRunTime(run.startedAt) },
            };
    }
}

/** Who started the run, how and when. */
export function runStartedText(run: EInvoiceRun): TranslatedText {
    return {
        key: `eInvoice.runStatus.startedBy.${run.source}`,
        params: { name: run.requestedByName ?? '-', time: formatRunTime(run.startedAt) },
    };
}

/** Why a failed run failed, as a translation key. */
export function runFailureKey(run: EInvoiceRun): string {
    return run.errorMessage !== null && isMessageKey(run.errorMessage)
        ? run.errorMessage
        : 'eInvoice.errors.runFailed';
}

/** The outcomes that happened, in a fixed order. */
export function runCountEntries(run: EInvoiceRun): Array<{ key: RunCountKey; count: number }> {
    return RUN_COUNT_KEYS.map(key => ({ key, count: run.counts[key] })).filter(
        entry => entry.count > 0,
    );
}

/** The statuses that have rows, in lifecycle order. */
export function statusCounts(counts: Partial<Record<EInvoiceStatus, number>>): StatusCount[] {
    return E_INVOICE_STATUSES.map(status => ({ status, count: counts[status] ?? 0 })).filter(
        entry => entry.count > 0,
    );
}

/** Rows a person has to look at: refused for good, or blocked after an interrupted send. */
export function attentionCount(counts: Partial<Record<EInvoiceStatus, number>>): number {
    return (counts.FAILED ?? 0) + (counts.BLOCKED ?? 0);
}

export interface SettingsFormValues {
    smartBillUsername: string;
    seriesName: string;
    receiptMode: boolean;
    receiptSeries: string;
    submitAfterDays: number;
    cancelWindowDays: number;
    sendsPerSecond: number;
    startDate: string | null;
    autoSubmit: boolean;
}

export function toSettingsForm(settings: EInvoiceSettings): SettingsFormValues {
    return {
        smartBillUsername: settings.smartBillUsername ?? '',
        seriesName: settings.seriesName ?? '',
        receiptMode: settings.receiptMode,
        receiptSeries: settings.receiptSeries ?? '',
        submitAfterDays: settings.submitAfterDays,
        cancelWindowDays: settings.cancelWindowDays,
        sendsPerSecond: settings.sendsPerSecond,
        startDate: settings.startDate,
        autoSubmit: settings.autoSubmit,
    };
}

/** The token is write-only: keep the saved one, replace it, or remove it. */
export type TokenChange =
    | { kind: 'keep' }
    | { kind: 'replace'; token: string }
    | { kind: 'remove' };

export function readTokenChange(newToken: string, remove: boolean): TokenChange {
    if (remove) {
        return { kind: 'remove' };
    }

    const token = newToken.trim();

    return token === '' ? { kind: 'keep' } : { kind: 'replace', token };
}

function emptyToNull(value: string): string | null {
    const trimmed = value.trim();

    return trimmed === '' ? null : trimmed;
}

/** `PUT /e-invoice/settings` body; an absent `smartBillToken` keeps the saved token. */
export function buildSettingsPayload(
    values: SettingsFormValues,
    tokenChange: TokenChange,
): Record<string, unknown> {
    const token =
        tokenChange.kind === 'keep'
            ? {}
            : { smartBillToken: tokenChange.kind === 'replace' ? tokenChange.token : null };

    return {
        provider: 'SMARTBILL',
        smartBillUsername: emptyToNull(values.smartBillUsername),
        seriesName: emptyToNull(values.seriesName),
        receiptMode: values.receiptMode,
        receiptSeries: emptyToNull(values.receiptSeries),
        submitAfterDays: values.submitAfterDays,
        cancelWindowDays: values.cancelWindowDays,
        sendsPerSecond: values.sendsPerSecond,
        startDate: values.startDate,
        autoSubmit: values.autoSubmit,
        ...token,
    };
}

/** `POST /e-invoice/import-legacy` body. */
export function buildImportRequest(
    dryRun: boolean,
    startDate: string | null,
): { dryRun: boolean; startDate?: string } {
    return startDate === null ? { dryRun } : { dryRun, startDate };
}

export function sendingModeKey(sending: SendingMode): string {
    return sending.live ? 'eInvoice.sending.live' : 'eInvoice.sending.simulated';
}

/** One row of `GET /e-invoice/invoices/status`: the state of one operation on one invoice. */
export interface InvoiceStatusRow {
    invoiceId: number;
    operation: EInvoiceOperation;
    status: EInvoiceStatus;
    providerSeries: string | null;
    providerNumber: string | null;
    errorCode: string | null;
}

/** The backend answers for at most this many invoices per request. */
export const STATUS_REQUEST_LIMIT = 200;

/** Distinct, positive invoice ids in the order first seen; `undefined` and `0` are dropped. */
export function distinctInvoiceIds(ids: readonly (number | null | undefined)[]): number[] {
    const distinct = new Set<number>();

    for (const id of ids) {
        if (id !== null && id !== undefined && id > 0) {
            distinct.add(id);
        }
    }

    return [...distinct];
}

/** Splits `ids` into requests the backend accepts. */
export function chunkInvoiceIds(ids: readonly number[]): number[][] {
    const chunks: number[][] = [];

    for (let start = 0; start < ids.length; start += STATUS_REQUEST_LIMIT) {
        chunks.push(ids.slice(start, start + STATUS_REQUEST_LIMIT));
    }

    return chunks;
}

/**
 * The rows per invoice. The backend lists an invoice's rows oldest first, so the last row is the
 * latest operation (a cancel follows its issue).
 */
export function groupStatusRows(
    rows: readonly InvoiceStatusRow[],
): ReadonlyMap<number, InvoiceStatusRow[]> {
    const grouped = new Map<number, InvoiceStatusRow[]>();

    for (const row of rows) {
        grouped.set(row.invoiceId, [...(grouped.get(row.invoiceId) ?? []), row]);
    }

    return grouped;
}

/** The row a list shows for an invoice: its latest operation; `null` when it was never queued. */
export function chipRow(rows: readonly InvoiceStatusRow[] | undefined): InvoiceStatusRow | null {
    return rows?.[rows.length - 1] ?? null;
}
